import { useForm } from "@inertiajs/react";
import React, { useEffect, useRef } from "react";

import FormModal from "@/components/Modal/FormModal";
import SearchSelect from "@/components/SearchSelect";
import TextFieldComponent from "@/components/TextField";
import { validateCNPJ } from "@/utils/cpfValidator";

type Option = {
  id: number | string;
  name: string;
};

export type PowerPlantData = {
  id: number;
  name: string;
  cnpj: string;
  facility_code: string;
  installed_capacity: number;
  consortium_id?: number;
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  mode?: "create" | "edit";
  powerPlant?: Partial<PowerPlantData>;
  consortiaOptions?: Option[];
  onSaved?: (message: string) => void;
  onError?: (message: string) => void;
};

type PowerPlantFormData = {
  name: string;
  consortium_id: string;
  cnpj: string;
  facility_code: string;
  installed_capacity: number | string;
};

const buildFormData = (
  powerPlant?: Partial<PowerPlantData>,
  consortiaOptions: Option[] = [],
): PowerPlantFormData => ({
  name: powerPlant?.name || "",
  consortium_id: powerPlant?.consortium_id
    ? String(powerPlant.consortium_id)
    : consortiaOptions[0]?.id
      ? String(consortiaOptions[0].id)
      : "",
  cnpj: powerPlant?.cnpj || "",
  facility_code: powerPlant?.facility_code || "",
  installed_capacity: powerPlant?.installed_capacity ?? 0,
});

type FormOptions = NonNullable<Parameters<ReturnType<typeof useForm>["post"]>[1]>;

export default function CreatePowerPlantModal({
  isOpen,
  onClose,
  mode = "create",
  powerPlant,
  consortiaOptions = [],
  onSaved,
  onError,
}: Props) {
  const isEdit = mode === "edit";

  const form = useForm(buildFormData(powerPlant, consortiaOptions));

  const { setData } = form;

  // Reseta o formulário apenas quando a modal abre; re-render da página
  // (ex.: redirect com erros de validação) não pode apagar o que foi digitado.
  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      setData(buildFormData(powerPlant, consortiaOptions));
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, powerPlant, consortiaOptions, setData]);

  const submit = () => {
    if (!form.data.name.trim()) {
      form.setError("name", "Informe o nome da usina.");
      return;
    }
    form.clearErrors("name");

    if (!form.data.consortium_id) {
      form.setError("consortium_id", "Selecione uma SPE.");
      return;
    }
    form.clearErrors("consortium_id");

    if (!validateCNPJ(form.data.cnpj)) {
      form.setError("cnpj", "Informe um CNPJ válido com 14 dígitos.");
      return;
    }
    form.clearErrors("cnpj");

    const onRequestError = (errors: Record<string, string | string[]>) => {
      const message = Object.values(errors).flat().join(" ");
      onError?.(message || "Não foi possível salvar a usina.");
    };

    const onSuccess: NonNullable<FormOptions["onSuccess"]> = () => {
      form.reset();
      onClose();
      onSaved?.(isEdit ? "Usina atualizada com sucesso." : "Usina criada com sucesso.");
    };

    if (isEdit && powerPlant?.id) {
      form.patch(`/manager/power-plants/${powerPlant.id}`, { onSuccess, onError: onRequestError });
    } else {
      form.post("/manager/power-plants", { onSuccess, onError: onRequestError });
    }
  };

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? "Editar usina" : "Adicionar usina"}
      submitButtonTitle={isEdit ? "Salvar alterações" : "Adicionar usina"}
      cancelButtonTitle="Cancelar"
      onSubmit={submit}
      isSubmitting={form.processing}
      size="sm"
    >
      <div className="grid grid-cols-1 gap-4">
        <TextFieldComponent
          labelPosition="top"
          label="Nome da usina"
          required
          type="text"
          placeholder="Digite o nome da usina"
          value={form.data.name}
          error={Boolean(form.errors.name)}
          helpText={form.errors.name}
          onChange={(event) => form.setData("name", event.target.value)}
        />

        <SearchSelect
          label="SPE"
          required
          options={consortiaOptions}
          value={form.data.consortium_id}
          error={Boolean(form.errors.consortium_id)}
          errorMessage={form.errors.consortium_id}
          onChange={(value) => form.setData("consortium_id", value)}
          placeholder="Seleciona uma SPE"
        />

        <TextFieldComponent
          labelPosition="top"
          label="CNPJ"
          required
          type="text"
          mask="cnpj"
          placeholder="00.000.000/0001-00"
          value={form.data.cnpj}
          error={Boolean(form.errors.cnpj)}
          helpText={form.errors.cnpj}
          onChange={(event) => form.setData("cnpj", event.target.value)}
        />

        <TextFieldComponent
          labelPosition="top"
          label="Número da instalação"
          required
          type="text"
          placeholder="Digite o número da instalação"
          value={form.data.facility_code}
          error={Boolean(form.errors.facility_code)}
          helpText={form.errors.facility_code}
          onChange={(event) => form.setData("facility_code", event.target.value)}
        />

        <TextFieldComponent
          labelPosition="top"
          label="Capacidade instalada (kW)"
          required
          type="number"
          min="0"
          step="1"
          placeholder="0"
          value={form.data.installed_capacity}
          error={Boolean(form.errors.installed_capacity)}
          helpText={form.errors.installed_capacity}
          onChange={(event) =>
            form.setData(
              "installed_capacity",
              event.target.value === "" ? "" : event.target.valueAsNumber,
            )
          }
        />
      </div>
    </FormModal>
  );
}
